/**
 * Back office → one invoice or quote: totals, PDF, shareable payment link, payment at the office,
 * Mobile Money request, payments (never deleted: cancelled with a reason), quote → invoice.
 */

import {
  ApiError,
  type InvoiceDetail,
  type MobileMethodEnum,
  type Payment,
  useBillingCashSessionsMineRetrieve,
  useBillingInvoicesCancelCreate,
  useBillingInvoicesIssueCreate,
  useBillingInvoicesMobileMoneyCreate,
  useBillingInvoicesPayCreate,
  useBillingInvoicesRetrieve,
  useBillingInvoicesSendCreate,
  useBillingInvoicesToInvoiceCreate,
  useBillingPaymentsCancelCreate,
} from "@agencyos/api-client";
import { formatXaf } from "@agencyos/core";
import { Badge, Button, GlassCard, GlassSheet, SelectField, Skeleton, TextField } from "@agencyos/ui-web";
import { Copy, FilePen, FileText, MessageCircle, Send, Smartphone, Stamp } from "lucide-react";
import { type FormEvent, useCallback, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Page } from "../components/AppShell";
import { localized } from "../components/TranslatedFields";
import { fieldErrors } from "../lib/settings";
import { InvoiceFormSheet } from "./billing/InvoiceFormSheet";
import {
  amountOf,
  dateText,
  dateTimeText,
  invoiceTone,
  isOverdue,
  OFFICE_METHODS,
  type OfficeMethod,
  paymentTone,
  rateText,
  useBillingRoles,
  useRefreshBilling,
} from "./billing/shared";

function errorText(error: unknown, fallback: string): string | undefined {
  if (!error) return undefined;
  if (error instanceof ApiError) {
    const fields = fieldErrors(error);
    return error.detail ?? Object.values(fields)[0] ?? fallback;
  }
  return fallback;
}

/** Asks for a reason before cancelling (the reason is kept with who and when). */
function ReasonSheet({
  open,
  title,
  intro,
  confirm,
  pending,
  error,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  intro: string;
  confirm: string;
  pending: boolean;
  error?: string;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [missing, setMissing] = useState(false);
  return (
    <GlassSheet open={open} title={title} onClose={onClose}>
      <form
        className="stack"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (!reason.trim()) return setMissing(true);
          setMissing(false);
          onConfirm(reason.trim());
        }}
      >
        <p className="muted">{intro}</p>
        <TextField
          label={t("billing.reason")}
          value={reason}
          maxLength={255}
          error={missing ? t("billing.reasonRequired") : error}
          onChange={(e) => setReason(e.target.value)}
        />
        <div className="row">
          <Button type="submit" disabled={pending}>
            {pending ? t("common.loading") : confirm}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
        </div>
      </form>
    </GlassSheet>
  );
}

function Totals({ invoice }: { invoice: InvoiceDetail }) {
  const { t, i18n } = useTranslation();
  const isInvoice = invoice.kind === "invoice";
  return (
    <GlassCard as="section" className="stack">
      <div className="section-title">
        <h2>{t("billing.detail.lines")}</h2>
        {invoice.document_url ? (
          <a className="ao-button ao-button--secondary" href={invoice.document_url} target="_blank" rel="noreferrer">
            <FileText size={16} aria-hidden="true" /> {t("billing.detail.pdf")}
          </a>
        ) : (
          <span className="muted">{t("billing.detail.pdfPending")}</span>
        )}
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>{t("billing.form.label")}</th>
              <th className="num">{t("billing.form.quantity")}</th>
              <th className="num">{t("billing.form.unitPrice")}</th>
              <th className="num">{t("billing.col.amount")}</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lines.map((line, index) => (
              <tr key={index}>
                <td>{typeof line.label === "string" ? line.label : localized(line.label, i18n.language)}</td>
                <td className="num">{line.quantity ?? 1}</td>
                <td className="num nowrap">{formatXaf(line.unit_price_xaf ?? line.amount_xaf ?? 0)}</td>
                <td className="num nowrap">{formatXaf(line.amount_xaf ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <dl className="totals">
        {invoice.discount_xaf ? (
          <>
            <dt>{t("billing.form.discount")}</dt>
            <dd>− {formatXaf(invoice.discount_xaf)}</dd>
          </>
        ) : null}
        <dt>{t("billing.detail.ht")}</dt>
        <dd>{formatXaf(invoice.total_ht_xaf)}</dd>
        <dt>{invoice.vat_rate_bp ? t("billing.detail.vatRate", { rate: rateText(invoice.vat_rate_bp, i18n.language) }) : t("billing.detail.noVat")}</dt>
        <dd>{formatXaf(invoice.vat_xaf)}</dd>
        <dt className="totals__strong">{t("billing.detail.ttc")}</dt>
        <dd className="totals__strong">{formatXaf(invoice.total_ttc_xaf)}</dd>
        {isInvoice ? (
          <>
            <dt>{t("billing.detail.paid")}</dt>
            <dd>{formatXaf(invoice.paid_xaf)}</dd>
            <dt className="totals__strong">{t("billing.detail.balance")}</dt>
            <dd className={`totals__strong${invoice.balance_xaf > 0 ? " ko" : " ok"}`}>{formatXaf(invoice.balance_xaf)}</dd>
          </>
        ) : null}
      </dl>
      <p className="muted">
        {t("billing.detail.issued", { date: dateText(invoice.created_at, i18n.language) })}
        {isInvoice && invoice.due_date ? ` · ${t("billing.detail.dueOn", { date: dateText(invoice.due_date, i18n.language) })}` : ""}
        {invoice.sent_at ? ` · ${t("billing.detail.sentAt", { date: dateTimeText(invoice.sent_at, i18n.language) })}` : ""}
      </p>
      {invoice.installments.length ? (
        <section className="stack" aria-label={t("billing.form.schedule")}>
          <h3>{t("billing.form.schedule")}</h3>
          <ul className="plain-list">
            {invoice.installments.map((row) => (
              <li key={row.due_date} className="row between">
                <span>{dateText(row.due_date, i18n.language)}</span>
                <span className="ao-num">{formatXaf(row.amount_xaf)}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {invoice.notes ? <p className="muted">{invoice.notes}</p> : null}
      {invoice.credited_invoice ? (
        <p role="note">
          {t("billing.credit.cancels")}{" "}
          <Link className="link" to={`/invoices/${invoice.credited_invoice}`}>
            {invoice.credited_number}
          </Link>
          {invoice.cancelled_reason ? ` — ${invoice.cancelled_reason}` : ""}
        </p>
      ) : null}
      {invoice.credit_notes.length ? (
        <p role="note">
          {t("billing.credit.cancelledBy")}{" "}
          {invoice.credit_notes.map((credit) => (
            <Link key={credit.id} className="link" to={`/invoices/${credit.id}`}>
              {credit.number}
            </Link>
          ))}
        </p>
      ) : null}
      {invoice.status === "cancelled" && invoice.cancelled_reason && !invoice.credited_invoice ? (
        <p role="note" className="ko">
          {t("billing.detail.cancelledBecause", { reason: invoice.cancelled_reason })}
        </p>
      ) : null}
    </GlassCard>
  );
}

function PayLink({ invoice }: { invoice: InvoiceDetail }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  if (!invoice.pay_url) return null;
  const message = t("billing.link.whatsappText", {
    number: invoice.number,
    amount: formatXaf(invoice.balance_xaf),
    url: invoice.pay_url,
  });
  const copy = () => {
    void navigator.clipboard
      ?.writeText(invoice.pay_url)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2500);
      })
      .catch(() => setCopied(false));
  };
  return (
    <GlassCard as="section" className="stack">
      <h2>{t("billing.link.title")}</h2>
      <p className="muted">{t("billing.link.intro")}</p>
      <TextField label={t("billing.link.label")} value={invoice.pay_url} readOnly onFocus={(e) => e.target.select()} />
      <div className="row">
        <Button variant="secondary" icon={<Copy size={16} aria-hidden="true" />} onClick={copy}>
          {copied ? t("billing.link.copied") : t("billing.link.copy")}
        </Button>
        <a className="ao-button ao-button--whatsapp" href={`https://wa.me/?text=${encodeURIComponent(message)}`} target="_blank" rel="noreferrer">
          <MessageCircle size={16} aria-hidden="true" /> {t("billing.link.whatsapp")}
        </a>
      </div>
    </GlassCard>
  );
}

function RecordPayment({ invoice }: { invoice: InvoiceDetail }) {
  const { t } = useTranslation();
  const refresh = useRefreshBilling();
  const pay = useBillingInvoicesPayCreate({ mutation: { onSuccess: refresh } });
  const desk = useBillingCashSessionsMineRetrieve({ query: { retry: false } });
  const [amount, setAmount] = useState(String(invoice.balance_xaf));
  useEffect(() => setAmount(String(invoice.balance_xaf)), [invoice.balance_xaf]);
  const [method, setMethod] = useState<OfficeMethod>("cash");
  const [payer, setPayer] = useState("");
  const [phone, setPhone] = useState("");
  const [reference, setReference] = useState("");
  const [localError, setLocalError] = useState<Record<string, string>>({});
  const deskClosed = desk.error instanceof ApiError && desk.error.status === 404;
  const errors = { ...fieldErrors(pay.error), ...localError };
  const detail = pay.error instanceof ApiError ? pay.error.detail : undefined;
  const cashRefused = method === "cash" && deskClosed;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const value = amountOf(amount);
    const next: Record<string, string> = {};
    if (!(value > 0)) next.amount_xaf = t("billing.pay.amountRequired");
    else if (value > invoice.balance_xaf) next.amount_xaf = t("billing.pay.amountTooHigh", { balance: formatXaf(invoice.balance_xaf) });
    if (method !== "cash" && !reference.trim()) next.reference = t("billing.pay.referenceRequired");
    const offline = method === "momo_mtn" || method === "momo_orange";
    setLocalError(next);
    if (Object.keys(next).length) return;
    pay.mutate(
      {
        id: invoice.id,
        data: {
          amount_xaf: value,
          method,
          ...(payer.trim() ? { payer_name: payer.trim() } : {}),
          ...(phone.trim() ? { payer_phone: phone.trim() } : {}),
          ...(reference.trim() ? { reference: reference.trim() } : {}),
          ...(offline ? { offline: true } : {}),
        },
      },
      {
        onSuccess: (response) => {
          if (response.status !== 201) return;
          setPayer("");
          setPhone("");
          setReference("");
          setAmount(String(Math.max(invoice.balance_xaf - value, 0)));
        },
      },
    );
  };

  return (
    <GlassCard as="section" className="stack">
      <h2>{t("billing.pay.title")}</h2>
      <p className="muted">{t("billing.pay.intro")}</p>
      <form className="stack" onSubmit={onSubmit} noValidate>
        <div className="form-grid">
          <TextField
            label={t("billing.pay.amount")}
            inputMode="numeric"
            value={amount ? formatXaf(amountOf(amount), { suffix: false }) : ""}
            hint={t("billing.pay.amountHint", { balance: formatXaf(invoice.balance_xaf) })}
            error={errors.amount_xaf}
            onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))}
          />
          <SelectField
            label={t("billing.pay.method")}
            value={method}
            error={errors.method}
            options={OFFICE_METHODS.map((m) => ({
              value: m,
              label: m === "momo_mtn" || m === "momo_orange" ? t("billing.pay.offlineMomo", { method: t(`billing.method.${m}`) }) : t(`billing.method.${m}`),
            }))}
            onChange={(e) => {
              setMethod(e.target.value as OfficeMethod);
              pay.reset();
            }}
          />
          {method !== "cash" ? (
            <TextField
              label={t("billing.pay.reference")}
              hint={method === "momo_mtn" || method === "momo_orange" ? t("billing.pay.offlineHint") : t("billing.pay.referenceHint")}
              value={reference}
              maxLength={100}
              error={errors.reference}
              onChange={(e) => setReference(e.target.value)}
            />
          ) : null}
          <TextField
            label={t("billing.pay.payer")}
            hint={t("billing.pay.payerHint", { client: invoice.client_name })}
            value={payer}
            maxLength={150}
            error={errors.payer_name}
            onChange={(e) => setPayer(e.target.value)}
          />
          <TextField
            label={t("billing.pay.payerPhone")}
            type="tel"
            inputMode="tel"
            value={phone}
            maxLength={30}
            error={errors.payer_phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>
        {cashRefused ? (
          <div role="alert" className="notice-warning billing-notice">
            <strong>{t("billing.pay.deskClosedTitle")}</strong>
            <span>{t("billing.pay.deskClosed")}</span>
            <Link className="link" to="/cash">
              {t("billing.pay.openDesk")}
            </Link>
          </div>
        ) : detail ? (
          <p role="alert" className="ko">
            {detail}
          </p>
        ) : null}
        <div className="row">
          <Button type="submit" disabled={pay.isPending}>
            {pay.isPending ? t("common.loading") : t("billing.pay.submit")}
          </Button>
          {pay.isSuccess ? (
            <span role="status" className="ok">
              {t("billing.pay.done")}
            </span>
          ) : null}
        </div>
      </form>
    </GlassCard>
  );
}

function MobileMoney({ invoice }: { invoice: InvoiceDetail }) {
  const { t } = useTranslation();
  const refresh = useRefreshBilling();
  const request = useBillingInvoicesMobileMoneyCreate({ mutation: { onSuccess: refresh } });
  const [method, setMethod] = useState<MobileMethodEnum>("momo_mtn");
  const [phone, setPhone] = useState("");
  const [payer, setPayer] = useState("");
  const [amount, setAmount] = useState(String(invoice.balance_xaf));
  useEffect(() => setAmount(String(invoice.balance_xaf)), [invoice.balance_xaf]);
  const errors = fieldErrors(request.error);
  const detail = request.error instanceof ApiError ? request.error.detail : undefined;
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const value = amountOf(amount);
    request.mutate({
      id: invoice.id,
      data: {
        method,
        phone: phone.trim(),
        ...(payer.trim() ? { payer_name: payer.trim() } : {}),
        ...(value > 0 ? { amount_xaf: value } : {}),
      },
    });
  };
  return (
    <GlassCard as="section" className="stack">
      <h2>
        <Smartphone size={18} aria-hidden="true" /> {t("billing.momo.title")}
      </h2>
      <p className="muted">{t("billing.momo.intro")}</p>
      <form className="stack" onSubmit={onSubmit} noValidate>
        <div className="form-grid">
          <SelectField
            label={t("billing.momo.operator")}
            value={method}
            error={errors.method}
            options={(["momo_mtn", "momo_orange"] as const).map((m) => ({ value: m, label: t(`billing.method.${m}`) }))}
            onChange={(e) => setMethod(e.target.value as MobileMethodEnum)}
          />
          <TextField
            label={t("billing.momo.phone")}
            type="tel"
            inputMode="tel"
            hint={t("billing.momo.phoneHint")}
            value={phone}
            maxLength={30}
            error={errors.phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <TextField label={t("billing.momo.payer")} value={payer} maxLength={150} error={errors.payer_name} onChange={(e) => setPayer(e.target.value)} />
          <TextField
            label={t("billing.pay.amount")}
            inputMode="numeric"
            value={amount ? formatXaf(amountOf(amount), { suffix: false }) : ""}
            error={errors.amount_xaf}
            onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))}
          />
        </div>
        {detail ? (
          <p role="alert" className="ko">
            {detail}
          </p>
        ) : null}
        <div className="row">
          <Button type="submit" disabled={request.isPending || !phone.trim()}>
            {request.isPending ? t("common.loading") : t("billing.momo.submit")}
          </Button>
          {request.isSuccess ? (
            <span role="status" className="ok">
              {t("billing.momo.sent")}
            </span>
          ) : null}
        </div>
      </form>
    </GlassCard>
  );
}

function Payments({ invoice }: { invoice: InvoiceDetail }) {
  const { t, i18n } = useTranslation();
  const { canCancel } = useBillingRoles();
  const refresh = useRefreshBilling();
  const cancel = useBillingPaymentsCancelCreate({ mutation: { onSuccess: refresh } });
  const [target, setTarget] = useState<Payment | null>(null);
  const { reset } = cancel;
  const close = useCallback(() => {
    setTarget(null);
    reset();
  }, [reset]);
  return (
    <GlassCard as="section" className="stack">
      <h2>{t("billing.payments.title")}</h2>
      {!invoice.payments.length ? <p className="muted">{t("billing.noPayment")}</p> : null}
      {invoice.payments.length ? (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{t("billing.col.date")}</th>
                <th>{t("billing.col.payer")}</th>
                <th>{t("billing.col.method")}</th>
                <th className="num">{t("billing.col.amount")}</th>
                <th>{t("billing.col.status")}</th>
                <th>{t("billing.col.receipt")}</th>
                {canCancel ? <th aria-label={t("billing.col.actions")} /> : null}
              </tr>
            </thead>
            <tbody>
              {invoice.payments.map((payment) => {
                const cancelled = payment.status === "cancelled";
                return (
                  <tr key={payment.id} className={cancelled ? "is-cancelled" : undefined}>
                    <td className="nowrap">{dateTimeText(payment.confirmed_at ?? payment.created_at, i18n.language)}</td>
                    <td>
                      <span className="amount">{payment.payer_name || invoice.client_name}</span>
                      {payment.third_party ? (
                        <>
                          {" "}
                          <Badge tone="offer">{t("billing.thirdParty")}</Badge>
                        </>
                      ) : null}
                      {payment.payer_phone ? <div className="muted">{payment.payer_phone}</div> : null}
                      {payment.received_by_name ? <div className="muted">{t("billing.payments.receivedBy", { name: payment.received_by_name })}</div> : null}
                    </td>
                    <td>
                      {t(`billing.method.${payment.method}`)}
                      {payment.reference ? <div className="muted">{payment.reference}</div> : null}
                    </td>
                    <td className="num nowrap">
                      <span className="amount">{formatXaf(payment.amount_xaf)}</span>
                    </td>
                    <td>
                      <div className="row">
                        <Badge tone={paymentTone(payment.status)}>{t(`billing.paymentStatus.${payment.status}`)}</Badge>
                        {payment.is_test ? <Badge>{t("billing.test")}</Badge> : null}
                      </div>
                      {cancelled ? (
                        <div className="muted">
                          {t("billing.payments.cancelledBy", {
                            name: payment.cancelled_by_name || "—",
                            date: dateTimeText(payment.cancelled_at, i18n.language),
                            reason: payment.cancelled_reason,
                          })}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      {payment.receipt_url ? (
                        <a className="link nowrap" href={payment.receipt_url} target="_blank" rel="noreferrer">
                          {payment.receipt_number || t("billing.receipt")}
                        </a>
                      ) : (
                        <span className="nowrap">{payment.receipt_number || "—"}</span>
                      )}
                    </td>
                    {canCancel ? (
                      <td>
                        {payment.status === "confirmed" || payment.status === "pending" ? (
                          <Button variant="ghost" onClick={() => setTarget(payment)}>
                            {t("billing.payments.cancel")}
                          </Button>
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      {target ? (
        <ReasonSheet
          open
          title={t("billing.payments.cancelTitle", { amount: formatXaf(target.amount_xaf) })}
          intro={t("billing.payments.cancelIntro")}
          confirm={t("billing.payments.confirmCancel")}
          pending={cancel.isPending}
          error={errorText(cancel.error, t("common.error"))}
          onClose={close}
          onConfirm={(reason) => cancel.mutate({ id: target.id, data: { reason } }, { onSuccess: () => setTarget(null) })}
        />
      ) : null}
    </GlassCard>
  );
}

export function InvoicePage() {
  const { t } = useTranslation();
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { canCollect, canCancel } = useBillingRoles();
  const refresh = useRefreshBilling();
  const invoice = useBillingInvoicesRetrieve(id);
  const send = useBillingInvoicesSendCreate({ mutation: { onSuccess: refresh } });
  const convert = useBillingInvoicesToInvoiceCreate({ mutation: { onSuccess: refresh } });
  const cancel = useBillingInvoicesCancelCreate({ mutation: { onSuccess: refresh } });
  const issue = useBillingInvoicesIssueCreate({ mutation: { onSuccess: refresh } });
  const [cancelling, setCancelling] = useState(false);
  const [editing, setEditing] = useState(false);
  const closeCancel = useCallback(() => setCancelling(false), []);

  if (invoice.isPending) {
    return (
      <Page>
        <Skeleton style={{ width: "40%", height: 28 }} />
        <Skeleton style={{ height: 200 }} />
      </Page>
    );
  }
  if (invoice.data?.status !== 200) {
    return (
      <Page title={t("billing.title")}>
        <GlassCard role="alert" className="stack">
          <p>{t("billing.detail.notFound")}</p>
          <Link className="link" to="/invoices">
            {t("billing.detail.back")}
          </Link>
        </GlassCard>
      </Page>
    );
  }
  const d = invoice.data.data;
  const isInvoice = d.kind === "invoice";
  const isDraft = d.status === "draft";
  const open = d.status === "issued" || d.status === "partial";
  const collectable = isInvoice && open && d.balance_xaf > 0;
  return (
    <Page
      title={`${t(`billing.kind.${d.kind}`)} ${d.number || t("billing.draftNumber")}`}
      description={
        <>
          <Link to="/invoices" className="link">
            {t("billing.title")}
          </Link>{" "}
          · {d.client_name}
          {d.contract_number ? ` · ${t("billing.detail.contract", { number: d.contract_number })}` : ""}
        </>
      }
      actions={
        <>
          <Badge tone={invoiceTone(d.status)}>{t(`billing.status.${d.status}`)}</Badge>
          {isOverdue(d) ? <Badge tone="danger">{t("billing.overdue")}</Badge> : null}
          {canCollect && isDraft ? (
            <>
              <Button icon={<Stamp size={16} aria-hidden="true" />} disabled={issue.isPending} onClick={() => issue.mutate({ id: d.id })}>
                {issue.isPending ? t("common.loading") : isInvoice ? t("billing.form.issueInvoice") : t("billing.form.issueQuote")}
              </Button>
              <Button variant="secondary" icon={<FilePen size={16} aria-hidden="true" />} onClick={() => setEditing(true)}>
                {t("billing.form.editDraft")}
              </Button>
            </>
          ) : null}
          {canCollect && d.status !== "cancelled" && !isDraft && d.kind !== "credit" ? (
            <Button variant="secondary" icon={<Send size={16} aria-hidden="true" />} disabled={send.isPending} onClick={() => send.mutate({ id: d.id })}>
              {send.isSuccess ? t("billing.detail.sent") : t("billing.detail.resend")}
            </Button>
          ) : null}
          {canCollect && !isInvoice && d.status === "issued" ? (
            <Button
              disabled={convert.isPending}
              onClick={() =>
                convert.mutate(
                  { id: d.id },
                  { onSuccess: (response) => response.status === 201 && navigate(`/invoices/${response.data.id}`) },
                )
              }
            >
              {t("billing.detail.convert")}
            </Button>
          ) : null}
          {canCancel && (open || isDraft) && d.kind !== "credit" ? (
            <Button variant="ghost" onClick={() => setCancelling(true)}>
              {isInvoice ? t("billing.detail.cancelInvoice") : t("billing.detail.cancelQuote")}
            </Button>
          ) : null}
        </>
      }
    >
      {isDraft ? (
        <p role="note" className="notice-warning">
          {t("billing.form.draftNote")}
        </p>
      ) : null}
      {issue.isError ? (
        <p role="alert" className="ko">
          {errorText(issue.error, t("common.error"))}
        </p>
      ) : null}
      {editing ? <InvoiceFormSheet open draft={d} onClose={() => setEditing(false)} /> : null}
      {convert.isError ? (
        <p role="alert" className="ko">
          {errorText(convert.error, t("common.error"))}
        </p>
      ) : null}
      {send.isError ? (
        <p role="alert" className="ko">
          {errorText(send.error, t("common.error"))}
        </p>
      ) : null}
      {d.quote ? (
        <p className="muted">
          <Link className="link" to={`/invoices/${d.quote}`}>
            {t("billing.detail.fromQuote")}
          </Link>
        </p>
      ) : null}
      <div className="grid">
        <Totals invoice={d} />
        {collectable ? <PayLink invoice={d} /> : null}
      </div>
      {collectable && canCollect ? (
        <div className="grid">
          <RecordPayment key={d.id} invoice={d} />
          <MobileMoney key={`momo-${d.id}`} invoice={d} />
        </div>
      ) : null}
      {isInvoice ? <Payments invoice={d} /> : null}
      {cancelling ? (
        <ReasonSheet
          open
          title={isInvoice ? t("billing.detail.cancelInvoice") : t("billing.detail.cancelQuote")}
          intro={isInvoice && !isDraft ? t("billing.credit.intro") : t("billing.detail.cancelIntro")}
          confirm={t("billing.detail.confirmCancel")}
          pending={cancel.isPending}
          error={errorText(cancel.error, t("common.error"))}
          onClose={closeCancel}
          onConfirm={(reason) => cancel.mutate({ id: d.id, data: { reason } }, { onSuccess: () => setCancelling(false) })}
        />
      ) : null}
    </Page>
  );
}
